import React, { useState, useEffect, useRef } from 'react';

interface TerminalTypingProps {
  text: string | string[];
  typingSpeed?: number;
  delayAfterType?: number;
  cursor?: string;
  loop?: boolean;
  className?: string;
  onComplete?: () => void;
}

const TerminalTyping: React.FC<TerminalTypingProps> = ({
  text,
  typingSpeed = 50,
  delayAfterType = 1000,
  cursor = '█',
  loop = false,
  className = '',
  onComplete
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [cursorVisible, setCursorVisible] = useState(true);
  
  // Ensure textLines is always valid and non-empty
  const textLines = Array.isArray(text) ? (text.length > 0 ? text : ['']) : [text || ''];
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cursorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
    };
  }, []);
  
  // Handle cursor blinking
  useEffect(() => {
    const blinkCursor = () => {
      cursorTimeoutRef.current = setInterval(() => {
        setCursorVisible(prev => !prev);
      }, 500);
    };
    
    blinkCursor();
    
    return () => {
      if (cursorTimeoutRef.current) clearInterval(cursorTimeoutRef.current);
    };
  }, []);
  
  // Handle typing animation
  useEffect(() => {
    // Safety check to ensure currentLineIndex is valid
    if (currentLineIndex >= textLines.length) {
      setCurrentLineIndex(0);
      return;
    }
    
    if (!isTyping) return;
    
    const currentLine = textLines[currentLineIndex] || '';
    
    if (displayText.length < currentLine.length) {
      // Continue typing the current line
      timeoutRef.current = setTimeout(() => {
        const nextChar = currentLine.charAt(displayText.length);
        const randomVariation = Math.random() * 50; // Add some randomness to typing speed
        
        // Sometimes add a typing "error" and correct it
        if (Math.random() < 0.01) {
          // Add a wrong character
          const wrongChar = 'abcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()'.charAt(
            Math.floor(Math.random() * 46)
          );
          
          setDisplayText(prev => prev + wrongChar);
          
          // Wait a bit and then remove it
          timeoutRef.current = setTimeout(() => {
            setDisplayText(prev => prev.slice(0, -1));
            
            // Type the correct character after a delay
            timeoutRef.current = setTimeout(() => {
              setDisplayText(prev => prev + nextChar);
            }, typingSpeed + randomVariation);
          }, 200);
        } else {
          // Normal typing
          setDisplayText(prev => prev + nextChar);
        }
      }, typingSpeed);
    } else {
      // Completed typing the current line
      timeoutRef.current = setTimeout(() => {
        if (currentLineIndex < textLines.length - 1) {
          // Move to the next line
          setCurrentLineIndex(prevIndex => prevIndex + 1);
          setDisplayText('');
        } else {
          // Completed all lines
          setIsTyping(false);
          if (onComplete) onComplete();
          
          // Handle loop if enabled
          if (loop) {
            timeoutRef.current = setTimeout(() => {
              setCurrentLineIndex(0);
              setDisplayText('');
              setIsTyping(true);
            }, delayAfterType * 2);
          }
        }
      }, delayAfterType);
    }
  }, [displayText, isTyping, currentLineIndex, textLines, typingSpeed, delayAfterType, loop, onComplete]);
  
  // Create terminal-style output with command prompting
  const renderTerminalOutput = () => {
    const lines = [];
    
    // Add completed lines
    for (let i = 0; i < currentLineIndex && i < textLines.length; i++) {
      const lineContent = textLines[i] || '';
      const isCommand = typeof lineContent === 'string' && lineContent.startsWith('$');
      
      lines.push(
        <div key={`line-${i}`} className="terminal-line">
          {isCommand ? (
            <>
              <span className="terminal-prompt">root@matrix:~#</span> {lineContent.substring(1)}
            </>
          ) : (
            lineContent
          )}
        </div>
      );
    }
    
    // Add current line being typed - with safety checks
    if (currentLineIndex < textLines.length) {
      const currentLineContent = textLines[currentLineIndex] || '';
      const isCurrentCommand = typeof currentLineContent === 'string' && currentLineContent.startsWith('$');
      
      lines.push(
        <div key="current-line" className="terminal-line active">
          {isCurrentCommand ? (
            <>
              <span className="terminal-prompt">root@matrix:~#</span> {displayText}
            </>
          ) : (
            displayText
          )}
          {cursorVisible && <span className="terminal-cursor">{cursor}</span>}
        </div>
      );
    }
    
    return lines;
  };
  
  return (
    <div className={`terminal-typing ${className}`}>
      {renderTerminalOutput()}
    </div>
  );
};

export default TerminalTyping;